import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar2 from '@/component/navbar2';
import Footer from '@/components/BrandFooter';
import { BRAND } from '@/brand';
import { useBlogPosts, useAutoNews, timeAgo, articleParagraphs, type BlogPost } from '@/data/blog';

/* Public blog. Main column = our own posts (blog/current, written in /admin/blog);
   the same store feeds the signed-in /articles view, so publishing once serves
   both. Right rail = industry headlines (news/auto) — link-out only, we never
   republish article bodies. Everything here is brand-split. */

const IS_CORP = BRAND === 'corporate';
const ACCENT = IS_CORP ? '#F08A24' : '#1976D2';
const ACCENT_D = IS_CORP ? '#C76B12' : '#0C447C';
const ACCENT_SOFT = IS_CORP ? '#FDE8CF' : '#DBEAFE';
const INK = '#17263A';
const INK2 = '#42505F';
const MUTED = '#6E7888';
const LINE = IS_CORP ? '#EAE3D4' : '#E2E8F0';
const CARD = '#FFFFFF';
const PAPER = IS_CORP ? '#FBF7EF' : '#F6F9FC';
const SHADOW = '0 10px 26px -16px rgba(23,38,58,.22)';
const SHADOW_L = '0 26px 60px -32px rgba(23,38,58,.34)';

const Wrap = ({ children, style, max = 1120 }: { children: React.ReactNode; style?: React.CSSProperties; max?: number }) => (
  <div className="mx-auto px-6" style={{ maxWidth: max, ...style }}>{children}</div>
);

const Pill = ({ children, tone = 'accent' }: { children: React.ReactNode; tone?: 'accent' | 'plain' }) => (
  <span style={{
    background: tone === 'accent' ? ACCENT_SOFT : '#F1F5F9',
    color: tone === 'accent' ? ACCENT_D : MUTED,
    fontWeight: 800, fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase',
    padding: '4px 10px', borderRadius: 999, whiteSpace: 'nowrap',
  }}>{children}</span>
);

const fmtDate = (iso?: string) => {
  const t = iso ? Date.parse(iso) : NaN;
  return Number.isNaN(t) ? '' : new Date(t).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

/** Deterministic wash so an image-less card still has a face. */
const wash = (seed: string) => {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) % 360;
  return `linear-gradient(135deg, hsl(${h} 42% 93%), hsl(${(h + 42) % 360} 48% 86%))`;
};

const Byline = ({ p, size = 12 }: { p: BlogPost; size?: number }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: size, color: MUTED, fontWeight: 600 }}>
    <span style={{
      width: 22, height: 22, borderRadius: '50%', background: ACCENT, color: '#fff',
      display: 'grid', placeItems: 'center', fontSize: 10, fontWeight: 800, flex: 'none',
    }}>{(p.author || 'C').trim().charAt(0).toUpperCase()}</span>
    <span>{p.author}</span>
    {p.publishedAt && <><span style={{ color: '#CBD5E1' }}>·</span><span>{fmtDate(p.publishedAt)}</span></>}
    {p.readTime && <><span style={{ color: '#CBD5E1' }}>·</span><span>{p.readTime}</span></>}
  </div>
);

// ── Industry news rail (auto-fetched, link-out) ──────────────────────────────
function NewsRail({ news }: { news: ReturnType<typeof useAutoNews> }) {
  if (!news.length) return null;
  return (
    <aside style={{ background: CARD, border: `1px solid ${LINE}`, borderRadius: 18, padding: 20, boxShadow: SHADOW, alignSelf: 'start', position: 'sticky', top: 96 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 }}>
        <h2 style={{ fontSize: 14, fontWeight: 800, color: INK }}>Industry news</h2>
        <span style={{ fontSize: 11, color: MUTED, fontWeight: 600 }}>{IS_CORP ? 'UAE tax & compliance' : 'Funding & VC'}</span>
      </div>
      <ul style={{ display: 'grid', gap: 14 }}>
        {news.map(n => (
          <li key={n.id} style={{ borderBottom: `1px solid ${LINE}`, paddingBottom: 12 }}>
            <a href={n.url} target="_blank" rel="noopener noreferrer nofollow" style={{ textDecoration: 'none', display: 'block' }}>
              <span style={{ fontSize: 10, fontWeight: 800, color: ACCENT_D, textTransform: 'uppercase', letterSpacing: '.08em' }}>{n.tag}</span>
              <p style={{ fontSize: 13.5, fontWeight: 700, color: INK, lineHeight: 1.45, margin: '4px 0 6px' }}>{n.title}</p>
              <span style={{ fontSize: 11.5, color: MUTED, fontWeight: 600 }}>
                {n.source}{n.publishedAt ? ` · ${timeAgo(n.publishedAt)}` : ''} ↗
              </span>
            </a>
          </li>
        ))}
      </ul>
      <p style={{ fontSize: 10.5, color: MUTED, marginTop: 12, lineHeight: 1.5 }}>
        Headlines link to the original publisher. Conncct is not the source.
      </p>
    </aside>
  );
}

// ── Featured (first post, image-led) ─────────────────────────────────────────
function Featured({ p }: { p: BlogPost }) {
  return (
    <Link to={`/blog/${p.slug}`} className="blog-feat" style={{
      display: 'grid', gridTemplateColumns: '1.05fr .95fr', gap: 0, textDecoration: 'none',
      background: CARD, border: `1px solid ${LINE}`, borderRadius: 22, overflow: 'hidden', boxShadow: SHADOW_L,
    }}>
      <div style={{ minHeight: 260, background: p.image ? undefined : wash(p.slug || p.title) }}>
        {p.image && <img src={p.image} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
      </div>
      <div style={{ padding: '30px 32px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 12 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Pill>{p.category || 'Insight'}</Pill>
          <Pill tone="plain">Latest</Pill>
        </div>
        <h2 style={{ fontSize: 26, fontWeight: 900, color: INK, lineHeight: 1.24, letterSpacing: '-.02em' }}>{p.title}</h2>
        {p.excerpt && <p style={{ fontSize: 14.5, color: INK2, lineHeight: 1.7 }}>{p.excerpt}</p>}
        <Byline p={p} />
        <span style={{ color: ACCENT_D, fontWeight: 800, fontSize: 13.5, marginTop: 4 }}>Read the article →</span>
      </div>
    </Link>
  );
}

// ── Card ─────────────────────────────────────────────────────────────────────
function PostCard({ p }: { p: BlogPost }) {
  return (
    <article style={{ background: CARD, border: `1px solid ${LINE}`, borderRadius: 18, overflow: 'hidden', boxShadow: SHADOW, display: 'flex', flexDirection: 'column' }}>
      <Link to={`/blog/${p.slug}`} style={{ display: 'block', height: 168, background: p.image ? undefined : wash(p.slug || p.title) }}>
        {p.image && <img src={p.image} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
      </Link>
      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 9, flex: 1 }}>
        <div><Pill>{p.category || 'Insight'}</Pill></div>
        <h2 style={{ fontSize: 17.5, fontWeight: 800, color: INK, lineHeight: 1.34, letterSpacing: '-.01em' }}>
          <Link to={`/blog/${p.slug}`} style={{ color: INK, textDecoration: 'none' }}>{p.title}</Link>
        </h2>
        {p.excerpt && (
          <p style={{ fontSize: 13.5, color: INK2, lineHeight: 1.65, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {p.excerpt}
          </p>
        )}
        <div style={{ marginTop: 'auto', paddingTop: 6 }}><Byline p={p} size={11.5} /></div>
      </div>
    </article>
  );
}

// ── Single article ───────────────────────────────────────────────────────────
function Article({ post, others }: { post: BlogPost; others: BlogPost[] }) {
  return (
    <>
      <article style={{ background: CARD, border: `1px solid ${LINE}`, borderRadius: 22, overflow: 'hidden', boxShadow: SHADOW_L }}>
        {post.image
          ? <img src={post.image} alt="" style={{ width: '100%', maxHeight: 400, objectFit: 'cover', display: 'block' }} />
          : <div style={{ height: 130, background: wash(post.slug || post.title) }} />}

        <div style={{ padding: '32px clamp(22px,4vw,54px) 40px' }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
            <Pill>{post.category || 'Insight'}</Pill>
          </div>
          <h1 style={{ fontSize: 'clamp(26px,3.6vw,38px)', fontWeight: 900, color: INK, lineHeight: 1.18, letterSpacing: '-.025em', marginBottom: 16 }}>
            {post.title}
          </h1>
          <div style={{ paddingBottom: 22, marginBottom: 26, borderBottom: `1px solid ${LINE}` }}>
            <Byline p={post} size={12.5} />
          </div>

          <div style={{ maxWidth: 760 }}>
            {articleParagraphs(post).map((para, i) => (
              <p key={i} style={{ fontSize: 16.5, color: INK2, lineHeight: 1.85, marginBottom: 18, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{para}</p>
            ))}
          </div>

          {/* Feed-drafted articles credit and link the publisher — we summarise
              and comment; the original reporting stays on their site. */}
          {post.sourceUrl && (
            <a href={post.sourceUrl} target="_blank" rel="noopener noreferrer nofollow"
               style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 8, padding: '11px 18px',
                        borderRadius: 12, background: ACCENT_SOFT, color: ACCENT_D, fontWeight: 800, fontSize: 13.5, textDecoration: 'none' }}>
              Read the original{post.sourceName ? ` at ${post.sourceName}` : ''} ↗
            </a>
          )}

          <div style={{ marginTop: 30, paddingTop: 22, borderTop: `1px solid ${LINE}` }}>
            <Link to="/blog" style={{ color: ACCENT_D, fontWeight: 800, fontSize: 14, textDecoration: 'none' }}>← All articles</Link>
          </div>
        </div>
      </article>

      {others.length > 0 && (
        <section style={{ marginTop: 34 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: INK, marginBottom: 16 }}>More from Conncct</h2>
          <div className="blog-more" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 18 }}>
            {others.slice(0, 3).map(p => <PostCard key={p.id} p={p} />)}
          </div>
        </section>
      )}
    </>
  );
}

export default function Blog() {
  const { slug } = useParams();
  const { posts, loading } = useBlogPosts();
  const news = useAutoNews();
  const [cat, setCat] = useState('All');

  const post = slug ? posts.find(p => p.slug === slug) : undefined;
  // The rail only earns its 320px when it has something in it, and never on a
  // single article — reserving the column regardless was squeezing the read.
  const showRail = !slug && news.length > 0;

  const categories = useMemo(
    () => ['All', ...Array.from(new Set(posts.map(p => p.category).filter(Boolean)))],
    [posts],
  );
  const visible = cat === 'All' ? posts : posts.filter(p => p.category === cat);
  const [featured, ...rest] = visible;

  return (
    <div style={{ background: PAPER, minHeight: '100vh' }}>
      <Navbar2 />

      {/* ── Hero ── */}
      <div style={{ background: `radial-gradient(900px 420px at 12% -30%, ${ACCENT_SOFT}, transparent 62%), ${PAPER}` }}>
        <Wrap max={slug ? 940 : 1120} style={{ paddingTop: 52, paddingBottom: slug ? 18 : 30 }}>
          <span style={{ fontWeight: 800, fontSize: 12, letterSpacing: '.16em', textTransform: 'uppercase', color: ACCENT_D }}>
            {IS_CORP ? 'Run & Comply' : 'Grow & Raise'}
          </span>
          {slug ? (
            /* The article's own <h1> is the page title — a second "Article"
               heading above it was pure noise. */
            <Link to="/blog" style={{ display: 'inline-block', marginTop: 10, color: ACCENT_D, fontWeight: 800, fontSize: 14, textDecoration: 'none' }}>
              ← All articles
            </Link>
          ) : (
            <h1 style={{ fontSize: 'clamp(30px,4.4vw,44px)', fontWeight: 900, color: INK, marginTop: 10, lineHeight: 1.12, letterSpacing: '-.03em' }}>
              {IS_CORP ? 'Tax, compliance & company insight' : 'Raising in MENA, explained'}
            </h1>
          )}
          {!slug && (
            <p style={{ fontSize: 16, color: INK2, marginTop: 12, maxWidth: 620, lineHeight: 1.7 }}>
              {IS_CORP
                ? 'Practical guidance on UAE tax, compliance and company setup — plus the headlines that affect your filings.'
                : 'How founders raise in MENA — readiness, structuring and the capital landscape, plus the funding news that matters.'}
            </p>
          )}

          {/* Category filter */}
          {!slug && categories.length > 2 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 22 }}>
              {categories.map(c => (
                <button key={c} onClick={() => setCat(c)} style={{
                  border: `1.5px solid ${cat === c ? ACCENT : LINE}`,
                  background: cat === c ? ACCENT : '#fff',
                  color: cat === c ? '#fff' : INK2,
                  fontWeight: 700, fontSize: 12.5, padding: '7px 14px', borderRadius: 999, cursor: 'pointer',
                }}>{c}</button>
              ))}
            </div>
          )}
        </Wrap>
      </div>

      <Wrap max={slug ? 940 : 1120} style={{ paddingBottom: 70 }}>
        <div className="blog-grid" style={{ display: 'grid', gap: 28, gridTemplateColumns: showRail ? 'minmax(0,1fr) 320px' : 'minmax(0,1fr)', alignItems: 'start' }}>
          <div style={{ display: 'grid', gap: 24, minWidth: 0 }}>
            {slug ? (
              post ? <Article post={post} others={posts.filter(p => p.slug !== post.slug)} /> : (
                <div style={{ background: CARD, border: `1px solid ${LINE}`, borderRadius: 18, padding: 34, textAlign: 'center' }}>
                  <p style={{ fontSize: 15, color: INK2, marginBottom: 12 }}>{loading ? 'Loading…' : 'That article isn’t available.'}</p>
                  {!loading && <Link to="/blog" style={{ color: ACCENT_D, fontWeight: 800, textDecoration: 'none' }}>← All articles</Link>}
                </div>
              )
            ) : loading ? (
              <p style={{ color: MUTED, fontWeight: 600 }}>Loading articles…</p>
            ) : visible.length ? (
              <>
                {featured && <Featured p={featured} />}
                {rest.length > 0 && (
                  <div className="blog-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 20 }}>
                    {rest.map(p => <PostCard key={p.id} p={p} />)}
                  </div>
                )}
              </>
            ) : (
              <div style={{ background: CARD, border: `1px dashed ${LINE}`, borderRadius: 18, padding: 40, textAlign: 'center' }}>
                <h2 style={{ fontSize: 18, fontWeight: 800, color: INK, marginBottom: 8 }}>No articles yet</h2>
                <p style={{ fontSize: 14, color: INK2, lineHeight: 1.7, maxWidth: 460, margin: '0 auto' }}>
                  Published articles appear here. {cat !== 'All' && 'Try another category.'}
                </p>
              </div>
            )}
          </div>

          {showRail && <NewsRail news={news} />}
        </div>
      </Wrap>

      {/* One column below the rail breakpoint so nothing squeezes the article. */}
      <style>{`
        @media (max-width: 980px){
          .blog-grid{ grid-template-columns: minmax(0,1fr) !important; }
          .blog-feat{ grid-template-columns: minmax(0,1fr) !important; }
          .blog-more{ grid-template-columns: repeat(2,1fr) !important; }
        }
        @media (max-width: 640px){
          .blog-cards{ grid-template-columns: minmax(0,1fr) !important; }
          .blog-more{ grid-template-columns: minmax(0,1fr) !important; }
        }
      `}</style>

      <Footer />
    </div>
  );
}
